import React, { memo } from 'react'
import { Icon } from '../../../atoms/Icon'
import { Badge } from '../../../atoms/Badge'
import { Text } from '../../../atoms/Text'
import {
  CardContainer,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  CardMeta,
  CardActions,
  CardPriority,
  CardAmount,
  CardOwner,
  CardTags,
  CardDragHandle,
} from './DealCard.styles'
import { DealCardProps } from './DealCard.types'
import { formatCurrency, formatDate } from '../../utils/deal.utils'

/**
 * DealCard Component
 *
 * A reusable card component for displaying deal information.
 * Supports both grid and kanban view modes with drag-and-drop functionality.
 *
 * @example
 * ```tsx
 * <DealCard
 *   deal={dealData}
 *   variant="kanban"
 *   onSelect={(deal) => handleDealSelect(deal)}
 *   draggable
 *   showActions
 * />
 * ```
 */
export const DealCard = memo<DealCardProps>(
  ({
    deal,
    variant = 'default',
    size = 'medium',
    selected = false,
    draggable = false,
    showOwner = true,
    showAmount = true,
    showPriority = true,
    showTags = true,
    showActions = false,
    customActions,
    onSelect,
    onEdit,
    onDelete,
    onDragStart,
    onDragEnd,
    className,
    style,
  }) => {
    const handleCardClick = (e: React.MouseEvent) => {
      if (!e.defaultPrevented && onSelect) {
        onSelect(deal)
      }
    }

    const handleActionClick = (e: React.MouseEvent, action: () => void) => {
      e.preventDefault()
      e.stopPropagation()
      action()
    }

    const getPriorityColor = (priority?: string) => {
      switch (priority) {
        case 'urgent':
          return '#ff3b30'
        case 'high':
          return '#ff9500'
        case 'medium':
          return '#007aff'
        case 'low':
          return '#34c759'
        default:
          return '#8e8e93'
      }
    }

    const getStatusBadgeVariant = (status?: string) => {
      switch (status) {
        case 'won':
          return 'success'
        case 'lost':
          return 'danger'
        case 'on_hold':
          return 'warning'
        default:
          return 'default'
      }
    }

    return (
      <CardContainer
        $variant={variant}
        $size={size}
        $selected={selected}
        $draggable={draggable}
        draggable={draggable}
        onDragStart={onDragStart ? (e) => onDragStart(e, deal) : undefined}
        onDragEnd={onDragEnd}
        onClick={handleCardClick}
        className={className}
        style={style}
      >
        {draggable && variant === 'kanban' && (
          <CardDragHandle>
            <Icon name="drag-handle" size="small" color="#8e8e93" />
          </CardDragHandle>
        )}

        <CardHeader>
          <div>
            <CardTitle>{deal.title}</CardTitle>
            {deal.organizationId && <CardMeta>{deal.organizationId}</CardMeta>}
          </div>

          {showPriority && deal.priority && (
            <CardPriority $color={getPriorityColor(deal.priority)}>
              <Icon
                name="priority"
                size="small"
                color={getPriorityColor(deal.priority)}
              />
            </CardPriority>
          )}
        </CardHeader>

        <CardBody>
          {deal.description && variant !== 'compact' && (
            <Text variant="body2" color="secondary">
              {deal.description}
            </Text>
          )}

          {showAmount && deal.amount && (
            <CardAmount>
              {formatCurrency(deal.amount, deal.currency)}
            </CardAmount>
          )}

          {showOwner && deal.ownerId && (
            <CardOwner>
              <Icon name="user" size="small" />
              <Text variant="body2">{deal.ownerId}</Text>
            </CardOwner>
          )}

          {deal.expectedCloseDate && (
            <CardMeta>
              <Icon name="calendar" size="small" />
              {formatDate(deal.expectedCloseDate)}
            </CardMeta>
          )}
        </CardBody>

        <CardFooter>
          <div>
            {deal.status && (
              <Badge variant={getStatusBadgeVariant(deal.status)}>
                {deal.status}
              </Badge>
            )}

            {showTags && deal.tags && deal.tags.length > 0 && (
              <CardTags>
                {deal.tags.slice(0, 3).map((tag) => (
                  <Badge key={tag} variant="default">
                    {tag}
                  </Badge>
                ))}
                {deal.tags.length > 3 && (
                  <Text variant="caption" color="secondary">
                    +{deal.tags.length - 3}
                  </Text>
                )}
              </CardTags>
            )}
          </div>

          {showActions && (
            <CardActions>
              {customActions?.map((action) => (
                <button
                  key={action.id}
                  onClick={(e) =>
                    handleActionClick(e, () => action.action(deal))
                  }
                  title={action.label}
                >
                  <Icon name={action.icon || 'more-vertical'} size="small" />
                </button>
              ))}
              {onEdit && (
                <button
                  onClick={(e) => handleActionClick(e, () => onEdit(deal))}
                  title="Edit"
                >
                  <Icon name="edit" size="small" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => handleActionClick(e, () => onDelete(deal.id))}
                  title="Delete"
                >
                  <Icon name="delete" size="small" />
                </button>
              )}
            </CardActions>
          )}
        </CardFooter>
      </CardContainer>
    )
  }
)

DealCard.displayName = 'DealCard'
